<template>
  <main class="pt-6">
    <form
      autocomplete="off"
      class="flex flex-col gap-6"
      @submit.prevent="submitForm()"
    >
      <fieldset v-if="!hasCourse" class="flex flex-col gap-2">
        <label for="title" class="font-semibold">
          {{ $t("classes.event.form.inputs.class") }}
        </label>

        <AutoComplete
          v-model="className"
          :class="
            vuelidate$.eventForm.classroom.$error
              ? 'p-invalid complete-custom'
              : 'complete-custom'
          "
          :placeholder="$t('classes.event.form.inputs.searchClass')"
          :suggestions="classes"
          optionLabel="name"
          @complete="searchClassByName"
          @item-select="onSelectClass"
          @clear="clearInputClass"
          dropdown
        />
        <Errors :errors="vuelidate$.eventForm.classroom.$errors" />
      </fieldset>

      <fieldset v-if="!hasCourse" class="flex flex-col gap-2">
        <label for="title" class="font-semibold">
          {{ $t("classes.event.form.inputs.course") }}
        </label>

        <AutoComplete
          v-model="courseName"
          :class="{ 'p-invalid': vuelidate$.eventForm.course.$error }"
          class="complete-custom"
          optionLabel="name"
          :placeholder="$t('classes.event.form.inputs.searchCourse')"
          :suggestions="courses"
          :forceSelection="true"
          :disabled="enableCourse"
          @complete="getCoursesByClassroomId(eventForm.classroom as string)"
          @item-select="onSelectCourse"
          dropdown
        />

        <Errors :errors="vuelidate$.eventForm.course.$errors" />
      </fieldset>

      <fieldset class="flex flex-col gap-2">
        <label for="title" class="font-semibold">
          {{ $t("classes.event.form.inputs.title") }}
        </label>
        <InputText
          type="text"
          id="title"
          v-model="eventForm.name"
          :class="{ 'p-invalid': vuelidate$.eventForm.name.$error }"
          :maxlength="50"
          :minLength="2"
          :placeholder="$t('classes.event.form.inputs.titlePlaceholder')"
        />
        <Errors
          :errors="vuelidate$.eventForm.name.$errors"
          :actualLength="eventForm.name.length"
          :maxLength="50"
          :minLength="2"
        />
      </fieldset>

      <div class="flex flex-col justify-between gap-6 sm:flex-row">
        <fieldset class="flex flex-col gap-2 w-full">
          <label for="date">
            {{ $t("classes.event.form.inputs.date") }}
          </label>
          <Calendar
            inputId="eventdate-input"
            v-model="eventForm.date"
            dateFormat="dd/mm/yy"
            :placeholder="$t('classes.event.form.inputs.datePlaceholder')"
            :manualInput="false"
            :minDate="today"
            :class="{ 'p-invalid': vuelidate$.eventForm.date.$error }"
          />
          <Errors :errors="vuelidate$.eventForm.date.$errors" />
        </fieldset>

        <div class="flex flex-row gap-6">
          <fieldset class="flex flex-col gap-2 max-w-20">
            <label for="startTime">{{
              $t("classes.event.form.inputs.startTime")
            }}</label>
            <InputMask
              id="startTime"
              v-model="eventForm.startTime"
              mask="99:99"
              placeholder="00:00"
              :class="vuelidate$.eventForm.startTime.$error ? 'p-invalid' : ''"
            />
            <Errors :errors="vuelidate$.eventForm.startTime.$errors" />
          </fieldset>
          <fieldset class="flex flex-col gap-2 max-w-20">
            <label for="endTime">{{
              $t("classes.event.form.inputs.endTime")
            }}</label>
            <InputMask
              id="endTime"
              v-model="eventForm.endTime"
              mask="99:99"
              placeholder="00:00"
              :class="{ 'p-invalid': vuelidate$.eventForm.endTime.$error }"
            />
            <Errors :errors="vuelidate$.eventForm.endTime.$errors" />
          </fieldset>
        </div>
      </div>
      <h6>{{ "Tipo de Aula" }}</h6>
      <div class="flex flex-row gap-4 items-center">
        <div class="flex align-items-center">
          <RadioButton
            v-model="classType"
            inputId="internalClass"
            name="classType"
            value="internal"
          />
          <label for="internalClass" class="ml-2">{{ "Aula interna" }}</label>
        </div>
        <div class="flex align-items-center">
          <RadioButton
            v-model="classType"
            inputId="externalClass"
            name="classType"
            value="external"
          />
          <label for="externalClass" class="ml-2">{{ "Aula externa" }}</label>
        </div>
      </div>
      <div v-if="isExternal">
        <fieldset class="flex flex-col justify-between gap-2">
          <label for="externalLink">
            {{ $t("classes.event.form.inputs.externalLink") }}
          </label>
          <InputText
            id="externalLink"
            v-model="eventForm.url"
            :maxlength="200"
            :placeholder="
              $t('classes.event.form.inputs.externalLinkPlaceholder')
            "
            :class="{ 'p-invalid': vuelidate$.eventForm.url.$error }"
          />
          <Errors :errors="vuelidate$.eventForm.url.$errors" />
          <div class="flex flex-row items-center gap-2">
            <v-icon name="md-info-outlined" :scale="0.8" />
            <p class="text-h-neutral-500 text-sm">
              {{
                $t("labels.generic", {
                  pt: "Não é permitido o cadastro de link recorrente",
                  en: "Recurring link registration is not allowed",
                })
              }}
            </p>
          </div>
        </fieldset>
      </div>
      <div class="flex flex-col gap-4" v-else>
        <fieldset class="flex flex-row justify-between gap-2">
          <v-icon name="md-peoplealt-round" :scale="1.2" />
          <p class="w-full">
            {{ $t("classes.event.form.inputs.public") }}
          </p>
          <InputSwitch v-model="eventForm.isPublic" />
        </fieldset>

        <fieldset class="flex flex-row justify-between gap-2">
          <v-icon name="md-videocameraback-round" :scale="1.2" />
          <p class="w-full">
            {{ $t("classes.event.form.inputs.record") }}
          </p>
          <InputSwitch v-model="eventForm.record" />
        </fieldset>
      </div>
      <div class="flex items-center justify-end w-full gap-2">
        <Button
          id="cancel-btn"
          :label="$t('classes.event.form.buttons.cancel')"
          class="btn-outline m-0"
          @click="onCloseForm()"
        />

        <Button
          id="submit-btn"
          type="submit"
          :label="$t('classes.event.form.buttons.save')"
          class="btn-primary m-0"
          :loading="loading"
        />
      </div>
    </form>
  </main>
</template>

<script lang="ts">
import { EventForm } from "@/models/agenda.model";
import { Course } from "@/models/courses.model";
import { QueryParams } from "@/models/query-params.model";
import { defineComponent, PropType } from "vue";
import { AgendaService } from "./agenda.service";
import { Classroom } from "@/models/classrooms.model";
import { AutoCompleteItemSelectEvent } from "primevue/autocomplete";

export default defineComponent({
  name: "formEvent",
  data() {
    return {
      today: new Date(),
      hasCourse: false as boolean,
      courseName: "",
      className: "",
      classes: [] as Array<Classroom>,
      coursesIds: [] as Array<string>,
      courses: [] as Array<Course>,
      params: new QueryParams(),
      record: [
        {
          name: this.$t("classes.event.form.inputs.confirm"),
          value: true,
        },
        {
          name: this.$t("classes.event.form.inputs.deny"),
          value: false,
        },
      ] as { name: string; value: boolean }[],
      isPublic: [
        {
          name: this.$t("classes.event.form.inputs.confirm"),
          value: true,
        },
        {
          name: this.$t("classes.event.form.inputs.deny"),
          value: false,
        },
      ] as { name: string; value: boolean }[],
      classType: "internal" as "internal" | "external",
    };
  },
  computed: {
    service(): AgendaService {
      return new AgendaService();
    },
    enableCourse(): boolean {
      return this.courses.length === 0;
    },
    courseId(): string {
      return this.$route.params.id as string;
    },
  },
  props: {
    eventForm: {
      type: Object as PropType<EventForm>,
      required: true,
    },
    vuelidate$: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isExternal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:eventForm", "onSubmit", "onClose", "update:isExternal"],
  inject: ["selectedClassId", "selectedCourseId"],
  watch: {
    className(newClass) {
      if (newClass) {
        this.clearInputClass();
        this.getCoursesByClassroomId(this.eventForm.classroom as string);
      }
    },
    classType(newValue) {
      if (newValue === "external") {
        this.$emit("update:isExternal", true);
        this.eventForm.isPublic = false;
        this.eventForm.record = false;
      } else {
        this.$emit("update:isExternal", false);
        this.eventForm.url = "";
      }
    },
  },
  methods: {
    submitForm(): void {
      this.$emit("onSubmit");
    },
    getCoursesByClassroomId(classroomId: string): void {
      if (!classroomId) {
        return;
      }
      this.service.courses.subscribe({
        next: (response) => {
          this.courses = response;
        },
      });
      this.service.getCoursesByClassroomId(classroomId as string);
    },
    onSelectCourse(event: AutoCompleteItemSelectEvent) {
      this.eventForm.course = event.value.id;
    },
    getClasses() {
      this.service.classes.subscribe({
        next: (response) => {
          this.classes = response.content;
        },
      });
      this.service.getClasses(this.params);
    },
    searchClassByName(inputCourse: { query: string }) {
      this.params.name = inputCourse.query;
      this.getClasses();
    },
    onSelectClass(event: AutoCompleteItemSelectEvent) {
      this.eventForm.classroom = event.value.id;
      this.getCoursesByClassroomId(event.value.id);
      this.clearInputClass();
    },
    clearInputClass(): void {
      this.courseName = "";
      this.courses = [];
      this.eventForm.course = "";
    },
    onCloseForm(): void {
      this.$emit("onClose");
      this.today = new Date();
      this.courseName = "";
      this.courses = [];
    },
  },
  mounted() {
    if (this.courseId) {
      this.eventForm.classroom = this.selectedClassId as string;
      this.eventForm.course = this.selectedCourseId as string;
      this.hasCourse = true;
    } else {
      this.getClasses();
      this.eventForm.classroom = "";
      this.eventForm.course = "";
    }
  },
});
</script>

<style lang="scss">
.complete-custom {
  .p-button {
    background-color: #153c59 !important;
    border: #153c59 !important;
  }
}
</style>
